import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useLeaveRequestPage, useLeaveValidation, useUserBalance } from '@/hooks/useLeaveRequestPage';
import { formatWorkingDays } from '@/components/attendance/WorkingDaysEditor';
import { EmploymentTypeBadge } from '@/components/EmploymentTypeSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  X,
  Users2,
  Clock,
  CalendarDays,
  Info,
  ChevronRight,
  Sparkles,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { REQUEST_STATUS, getStatusString } from '@/lib/attendanceTypes';
import { motion, AnimatePresence } from 'framer-motion';

// Balance display component
const BalanceDisplay = ({ balance, loading, label }) => {
  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-3 bg-slate-200 rounded w-24 mb-2" />
        <div className="h-8 bg-slate-200 rounded w-16" />
      </div>
    );
  }

  if (!balance) return null;

  return (
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-2xl font-bold text-slate-800 tabular-nums">{balance.remaining_days}</p>
      <p className="text-xs text-slate-500 mt-0.5">of {balance.entitled_days} days</p>
    </div>
  );
};

// Warning/Info message component
const AlertMessage = ({ type, title, description }) => {
  const styles = {
    error: {
      bg: 'bg-white',
      border: 'border-red-200',
      accent: 'bg-red-500',
      icon: 'text-red-500',
      title: 'text-red-800',
      desc: 'text-red-600'
    },
    warning: {
      bg: 'bg-white',
      border: 'border-amber-200',
      accent: 'bg-amber-500',
      icon: 'text-amber-500',
      title: 'text-amber-800',
      desc: 'text-amber-600'
    },
    info: {
      bg: 'bg-white',
      border: 'border-slate-200',
      accent: 'bg-slate-400',
      icon: 'text-slate-500',
      title: 'text-slate-800',
      desc: 'text-slate-600'
    },
    success: {
      bg: 'bg-white',
      border: 'border-emerald-200',
      accent: 'bg-emerald-500',
      icon: 'text-emerald-500',
      title: 'text-emerald-800',
      desc: 'text-emerald-600'
    }
  };

  const s = styles[type] || styles.info;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative ${s.bg} border ${s.border} rounded-xl overflow-hidden`}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${s.accent}`} />
      <div className="flex items-start gap-3 p-4 pl-5">
        <AlertCircle className={`h-5 w-5 ${s.icon} flex-shrink-0 mt-0.5`} />
        <div>
          <p className={`text-sm font-medium ${s.title}`}>{title}</p>
          {description && <p className={`text-xs ${s.desc} mt-1`}>{description}</p>}
        </div>
      </div>
    </motion.div>
  );
};

// Leave request item in the list
const LeaveRequestItem = ({ request, leaveTypes, formatDate, formatPeriodSuffix, onRemove, index }) => {
  const leaveType = leaveTypes.find(t => t.name === request.leave_type);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="group relative bg-white rounded-xl border border-slate-200 overflow-hidden
        hover:border-slate-300 hover:shadow-[0_4px_20px_-8px_rgba(0,0,0,0.1)] transition-all duration-200"
    >
      <div className="flex items-stretch">
        {/* Color accent */}
        <div
          className="w-1.5 flex-shrink-0"
          style={{ backgroundColor: leaveType?.color || '#94a3b8' }}
        />

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm font-semibold text-slate-800">
              {request.leave_type}{formatPeriodSuffix(request)}
            </span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
              {request.days} {request.days === 1 ? 'day' : 'days'}
            </span>
          </div>

          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span className="tabular-nums">{formatDate(request.start_date)}</span>
            <span className="text-slate-300">→</span>
            <span className="tabular-nums">{formatDate(request.end_date)}</span>
          </div>

          {request.reason && (
            <p className="text-xs text-slate-500 mt-2 pl-3 border-l-2 border-slate-200 line-clamp-1">
              {request.reason}
            </p>
          )}
        </div>

        {/* Remove button */}
        <button
          onClick={() => onRemove(request.id)}
          className="flex items-center justify-center px-4 border-l border-slate-100
            text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
};

export const RequestLeavePage = () => {
  const navigate = useNavigate();
  const { userProfile, company, can } = useAuth();
  const canManageAttendance = can('attendance.manage');

  usePageTitle('Request Leave');

  // Use optimized hooks for initial data loading
  const {
    loading: loadingPageData,
    leaveTypes,
    balance: initialBalance,
    companyUsers
  } = useLeaveRequestPage(userProfile?.id, company?.id, canManageAttendance);

  // Employment type state for leave eligibility
  const [employmentType, setEmploymentType] = useState(null);
  const [loadingEmploymentType, setLoadingEmploymentType] = useState(true);

  // Fetch user's employment type
  useEffect(() => {
    const fetchEmploymentType = async () => {
      if (!userProfile?.id || !company?.id) return;

      try {
        setLoadingEmploymentType(true);
        const { data, error } = await supabase
          .from('company_members')
          .select(`
            employment_type_id,
            employment_types (
              id, name, code, color, description,
              has_leave_entitlement, default_annual_leave
            )
          `)
          .eq('user_id', userProfile.id)
          .eq('company_id', company.id)
          .single();

        if (error) throw error;
        setEmploymentType(data?.employment_types || null);
      } catch (err) {
        console.error('Error fetching employment type:', err);
      } finally {
        setLoadingEmploymentType(false);
      }
    };

    fetchEmploymentType();
  }, [userProfile?.id, company?.id]);

  // Check if user is eligible for leave based on employment type
  const isEligibleForLeave = useMemo(() => {
    if (loadingEmploymentType) return true; // Assume eligible while loading
    if (!employmentType) return true; // Default to eligible if no type set
    return employmentType.has_leave_entitlement === true;
  }, [employmentType, loadingEmploymentType]);

  // Filter leave types based on eligibility - non-entitled users only see non-chargeable types
  const availableLeaveTypes = useMemo(() => {
    if (isEligibleForLeave) return leaveTypes;
    return leaveTypes.filter(t => !t.deducts_from_balance);
  }, [leaveTypes, isEligibleForLeave]);

  // Consolidated form state
  const [formData, setFormData] = useState({
    leave_type: '',
    start_date: '',
    end_date: '',
    start_period: 'full',
    end_period: 'full',
    reason: ''
  });

  // Admin user selection
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // Pending requests list
  const [leaveRequests, setLeaveRequests] = useState([]);

  // Submission state
  const [submitting, setSubmitting] = useState(false);

  // Target user for validation (first selected user or current user)
  const targetUserId = useMemo(() => {
    if (canManageAttendance && selectedUserIds.length > 0) {
      return selectedUserIds[0];
    }
    return userProfile?.id;
  }, [canManageAttendance, selectedUserIds, userProfile?.id]);

  // Use balance hook for target user (handles user switching)
  const { balance: targetBalance, loading: loadingBalance } = useUserBalance(targetUserId);

  // Final balance (initial balance for current user, target balance for admin-selected user)
  const balance = useMemo(() => {
    if (canManageAttendance && selectedUserIds.length > 0 && selectedUserIds[0] !== userProfile?.id) {
      return targetBalance;
    }
    return initialBalance;
  }, [canManageAttendance, selectedUserIds, userProfile?.id, targetBalance, initialBalance]);

  // Use optimized validation hook with 300ms debounce
  const {
    validate,
    validating,
    previewDays,
    usersOnLeave,
    currentUserOverlap,
    resetValidation,
    workingDays
  } = useLeaveValidation(targetUserId, company?.id, 300);

  // Set default leave type when loaded (wait for employment type to resolve first)
  const hasSetDefaultLeaveType = useRef(false);
  useEffect(() => {
    if (loadingEmploymentType) return; // Wait until we know eligibility
    if (availableLeaveTypes.length === 0) return;

    // Set default on first load, or reset if current selection is no longer available
    if (!hasSetDefaultLeaveType.current || !availableLeaveTypes.find(t => t.name === formData.leave_type)) {
      hasSetDefaultLeaveType.current = true;
      setFormData(prev => ({ ...prev, leave_type: availableLeaveTypes[0].name }));
    }
  }, [availableLeaveTypes, loadingEmploymentType, formData.leave_type]);

  // Auto-select current user for admins (only on initial load)
  const hasAutoSelected = useRef(false);
  useEffect(() => {
    if (canManageAttendance && userProfile?.id && companyUsers.length > 0 && !hasAutoSelected.current) {
      hasAutoSelected.current = true;
      setSelectedUserIds([userProfile.id]);
    }
  }, [canManageAttendance, userProfile?.id, companyUsers.length]);

  // Trigger validation when dates change
  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      validate(formData.start_date, formData.end_date, formData.start_period, formData.end_period);
    } else {
      resetValidation();
    }
  }, [formData.start_date, formData.end_date, formData.start_period, formData.end_period, validate, resetValidation]);

  // Computed warnings (memoized)
  const warnings = useMemo(() => {
    const result = {
      pastDate: false,
      overlap: false,
      insufficientBalance: false,
      overlapDetails: ''
    };

    if (!formData.start_date || !formData.end_date) {
      return result;
    }

    // Check for past dates (admins can enter past dates for sickness etc.)
    if (!canManageAttendance) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(formData.start_date);

      if (startDate < today) {
        result.pastDate = true;
      }
    }

    // Check for overlaps with already added requests
    const newStart = new Date(formData.start_date);
    const newEnd = new Date(formData.end_date);

    for (const existingReq of leaveRequests) {
      const existingStart = new Date(existingReq.start_date);
      const existingEnd = new Date(existingReq.end_date);

      if (newStart <= existingEnd && newEnd >= existingStart) {
        result.overlap = true;
        result.overlapDetails = `${existingReq.start_date} to ${existingReq.end_date}`;
        break;
      }
    }

    // Check balance for annual leave
    if (formData.leave_type === 'Annual Leave' && previewDays && balance) {
      const totalExistingAnnual = leaveRequests
        .filter(r => r.leave_type === 'Annual Leave')
        .reduce((sum, r) => sum + r.days, 0);

      const totalWithNew = totalExistingAnnual + previewDays;

      if (totalWithNew > balance.remaining_days) {
        result.insufficientBalance = true;
      }
    }

    return result;
  }, [formData.start_date, formData.end_date, formData.leave_type, leaveRequests, previewDays, balance]);

  // Selected leave type info (memoized)
  const selectedLeaveType = useMemo(() => {
    return leaveTypes.find(t => t.name === formData.leave_type);
  }, [leaveTypes, formData.leave_type]);

  const shouldShowBalance = useMemo(() => {
    if (!isEligibleForLeave) return false;
    return selectedLeaveType?.deducts_from_balance && selectedLeaveType?.name === 'Annual Leave';
  }, [selectedLeaveType, isEligibleForLeave]);

  // Memoized formatDate helper
  const formatDate = useCallback((dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  // Format period suffix for leave type display
  const formatPeriodSuffix = useCallback((request) => {
    const isSameDay = request.start_date === request.end_date;
    const startPeriod = request.start_period || 'full';
    const endPeriod = request.end_period || 'full';

    if (isSameDay) {
      if (startPeriod === 'am') return ' (AM)';
      if (startPeriod === 'pm') return ' (PM)';
      return '';
    } else {
      const startSuffix = startPeriod === 'am' ? 'AM' : startPeriod === 'pm' ? 'PM' : '';
      const endSuffix = endPeriod === 'am' ? 'AM' : endPeriod === 'pm' ? 'PM' : '';

      if (startSuffix && endSuffix) {
        return ` (${startSuffix} - ${endSuffix})`;
      } else if (startSuffix) {
        return ` (${startSuffix} start)`;
      } else if (endSuffix) {
        return ` (${endSuffix} end)`;
      }
      return '';
    }
  }, []);

  // Calculate total days
  const totalDays = useMemo(() => {
    return leaveRequests.reduce((sum, req) => sum + req.days, 0);
  }, [leaveRequests]);

  // Handle adding request to list
  const handleAddToList = useCallback((e) => {
    e.preventDefault();

    if (canManageAttendance && selectedUserIds.length === 0) {
      toast.error('Please select at least one user first');
      return;
    }

    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error('End date must be after start date');
      return;
    }

    if (!previewDays) {
      toast.error('Please wait for day calculation');
      return;
    }

    if (warnings.pastDate) {
      toast.error('Cannot request leave for past dates');
      return;
    }

    if (warnings.overlap) {
      toast.error(`This date range overlaps with an already added request`);
      return;
    }

    if (currentUserOverlap && selectedUserIds.length <= 1) {
      const userName = canManageAttendance && selectedUserIds.length === 1 && selectedUserIds[0] !== userProfile?.id
        ? companyUsers.find(u => u.id === selectedUserIds[0])?.name
        : 'You';
      toast.error(`${userName} already ${userName === 'You' ? 'have' : 'has'} leave booked during this period`);
      return;
    }

    const newRequest = {
      id: Date.now(),
      leave_type: formData.leave_type,
      start_date: formData.start_date,
      end_date: formData.end_date,
      start_period: formData.start_period,
      end_period: formData.end_period,
      reason: formData.reason,
      days: previewDays
    };

    setLeaveRequests(prev => [...prev, newRequest]);

    // Reset form
    setFormData({
      leave_type: availableLeaveTypes.length > 0 ? availableLeaveTypes[0].name : '',
      start_date: '',
      end_date: '',
      start_period: 'full',
      end_period: 'full',
      reason: ''
    });

    toast.success('Leave period added');
  }, [canManageAttendance, selectedUserIds, formData, previewDays, warnings, currentUserOverlap, userProfile?.id, companyUsers, availableLeaveTypes]);

  // Handle removing request from list
  const handleRemoveRequest = useCallback((id) => {
    setLeaveRequests(prev => prev.filter(r => r.id !== id));
    toast.info('Leave period removed');
  }, []);

  // Handle submit all
  const handleSubmitAll = async () => {
    if (leaveRequests.length === 0) {
      toast.error('Please add at least one leave period');
      return;
    }

    if (!userProfile?.id) {
      toast.error('User profile not loaded. Please refresh the page.');
      return;
    }

    const targetUserIds = canManageAttendance && selectedUserIds.length > 0
      ? selectedUserIds
      : [userProfile.id];

    if (targetUserIds.length === 0) {
      toast.error('No user selected for leave request');
      return;
    }

    setSubmitting(true);

    try {
      // Check for overlaps with existing requests
      const { data: existingRequests, error: checkError } = await supabase
        .from('leave_requests')
        .select('start_date, end_date, status')
        .eq('user_id', targetUserIds[0])
        .in('status', ['pending', 'approved']);

      if (checkError) throw checkError;

      for (const newReq of leaveRequests) {
        const newStart = new Date(newReq.start_date);
        const newEnd = new Date(newReq.end_date);

        for (const existing of existingRequests || []) {
          const existingStart = new Date(existing.start_date);
          const existingEnd = new Date(existing.end_date);

          if (newStart <= existingEnd && newEnd >= existingStart) {
            toast.error(`Cannot submit: ${newReq.start_date} to ${newReq.end_date} overlaps with an existing ${existing.status} request`);
            setSubmitting(false);
            return;
          }
        }
      }

      // Validate annual leave balance
      const annualLeaveRequests = leaveRequests.filter(r => r.leave_type === 'Annual Leave');
      const totalAnnualDays = annualLeaveRequests.reduce((sum, req) => sum + req.days, 0);

      if (totalAnnualDays > 0 && balance) {
        if (totalAnnualDays > balance.remaining_days) {
          toast.error(`Insufficient leave balance. You need ${totalAnnualDays} days but only have ${balance.remaining_days} remaining.`);
          setSubmitting(false);
          return;
        }
      }

      // Build requests for all users
      const requestsToInsert = [];
      const isAdminRequest = canManageAttendance;
      const status = isAdminRequest ? REQUEST_STATUS.APPROVED : REQUEST_STATUS.PENDING;

      for (const userId of targetUserIds) {
        for (const req of leaveRequests) {
          let partDayJson = null;
          if (req.start_period !== 'full' || req.end_period !== 'full') {
            partDayJson = {};
            if (req.start_period !== 'full') {
              partDayJson.start = req.start_period.toUpperCase();
            }
            if (req.end_period !== 'full' && req.end_date !== req.start_date) {
              partDayJson.end = req.end_period.toUpperCase();
            }
          }

          requestsToInsert.push({
            user_id: userId,
            leave_type: req.leave_type,
            start_date: req.start_date,
            end_date: req.end_date,
            part_day: partDayJson,
            user_note: req.reason || null,
            status: getStatusString(status),
            ...(isAdminRequest && {
              decided_date: new Date().toISOString(),
              decided_by: userProfile.id
            })
          });
        }
      }

      const { data: insertedRequests, error } = await supabase
        .from('leave_requests')
        .insert(requestsToInsert)
        .select();

      if (error) throw error;

      // Send notifications (batch user profile lookup to avoid N+1)
      if (insertedRequests && insertedRequests.length > 0) {
        const uniqueUserIds = [...new Set(insertedRequests.map(r => r.user_id))];

        // Single query for all user profiles
        const { data: usersData } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, email')
          .in('id', uniqueUserIds);

        const usersMap = new Map((usersData || []).map(u => [u.id, u]));

        // Send notifications
        for (const request of insertedRequests) {
          const userData = usersMap.get(request.user_id);
          if (!userData) continue;

          const isAdminCreatingForOthers = canManageAttendance && request.user_id !== userProfile.id;
          const leaveType = isAdminCreatingForOthers ? 'created_behalf' : 'submitted';
          const employeeName = `${userData.first_name} ${userData.last_name}`;

          const startDate = new Date(request.start_date).toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
          });
          const endDate = new Date(request.end_date).toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
          });

          try {
            await supabase.functions.invoke('process-notification', {
              body: {
                type: 'leave',
                leave_type: leaveType,
                user_ids: [request.user_id],
                title: isAdminCreatingForOthers
                  ? 'Leave Added to Your Calendar'
                  : 'Leave Request Submitted',
                message: isAdminCreatingForOthers
                  ? `${userProfile.first_name} ${userProfile.last_name} added leave to your calendar: ${request.leave_type}`
                  : `Your leave request has been submitted and is pending approval`,
                link_url: `/attendance/history`,
                entity_type: 'leave_request',
                entity_id: request.id,
                actor_id: userProfile.id,
                template_data: {
                  employee_name: employeeName,
                  leave_type: request.leave_type,
                  start_date: startDate,
                  end_date: endDate,
                  total_days: leaveRequests.find(r => r.start_date === request.start_date)?.days || 0,
                  submission_date: new Date().toLocaleDateString('en-GB', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                  }),
                  creator_name: `${userProfile.first_name} ${userProfile.last_name}`,
                  admin_notes: request.user_note || '',
                  app_url: window.location.origin,
                  short_link: `${window.location.origin}/attendance/history`
                }
              }
            });
          } catch (notifError) {
            console.error('Failed to send leave notification:', notifError);
          }
        }

        // Send N8N notification to admins for pending leave requests (not admin-created)
        if (!canManageAttendance) {
          try {
            // Get admins and managers from company_members
            const { data: adminMembers } = await supabase
              .from('company_members')
              .select(`
                user_id,
                role,
                user_profiles!inner (
                  id,
                  first_name,
                  last_name,
                  email
                )
              `)
              .eq('company_id', company.id)
              .in('role', ['Admin', 'Manager']);

            if (adminMembers && adminMembers.length > 0) {
              // Get unique requests to notify about (avoid duplicates if multiple users)
              const uniqueRequests = insertedRequests.filter((req, idx, arr) =>
                arr.findIndex(r => r.start_date === req.start_date && r.end_date === req.end_date && r.leave_type === req.leave_type) === idx
              );

              for (const request of uniqueRequests) {
                const userData = usersMap.get(request.user_id);
                if (!userData) continue;

                // Find the original request to get period info
                const originalRequest = leaveRequests.find(r => r.start_date === request.start_date);
                const totalDays = originalRequest?.days || 0;

                // Build leave type with AM/PM suffix
                const periodSuffix = formatPeriodSuffix(originalRequest || {});
                const leaveTypeWithPeriod = `${request.leave_type}${periodSuffix}`;

                const startDateFormatted = new Date(request.start_date).toLocaleDateString('en-GB', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                });
                const endDateFormatted = new Date(request.end_date).toLocaleDateString('en-GB', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                });
                const submittedDate = new Date().toLocaleDateString('en-GB', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                });

                // Send to each admin
                for (const admin of adminMembers) {
                  const adminProfile = admin.user_profiles;
                  if (!adminProfile?.email) continue;

                  try {
                    await fetch('https://n8n.add.ai/webhook/leave-admin-noti', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        AdminEmail: adminProfile.email,
                        AdminName: `${adminProfile.first_name} ${adminProfile.last_name}`,
                        FirstName: userData.first_name,
                        LastName: userData.last_name,
                        LeaveType: leaveTypeWithPeriod,
                        StartDate: startDateFormatted,
                        EndDate: endDateFormatted,
                        TotalDays: totalDays,
                        SubmittedDate: submittedDate
                      })
                    });
                  } catch (webhookError) {
                    console.error('Failed to send admin notification webhook:', webhookError);
                  }
                }
              }
            }
          } catch (adminNotifError) {
            console.error('Failed to fetch admins for notification:', adminNotifError);
          }
        }
      }

      // Success message
      let successMessage = `${insertedRequests.length} leave request(s) submitted successfully`;
      if (canManageAttendance && selectedUserIds.length > 0) {
        if (selectedUserIds.length === 1) {
          const selectedUser = companyUsers.find(u => u.id === selectedUserIds[0]);
          if (selectedUser) {
            successMessage = `${leaveRequests.length} leave period(s) submitted successfully for ${selectedUser.name}`;
          }
        } else {
          successMessage = `${leaveRequests.length} leave period(s) submitted successfully for ${selectedUserIds.length} users`;
        }
      }

      toast.success(successMessage);
      navigate(`/attendance/history`);
    } catch (error) {
      console.error('Failed to submit requests:', error);
      toast.error('Failed to submit leave requests');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle user selection change
  const handleUserSelectionChange = useCallback((userId, checked) => {
    setSelectedUserIds(prev => {
      const newIds = checked
        ? [...prev, userId]
        : prev.filter(id => id !== userId);
      return newIds;
    });
    setLeaveRequests([]);
  }, []);

  return (
    <div className="max-w-2xl space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Request Leave</h1>
        <p className="text-slate-500 mt-1">Submit a new leave request for approval</p>
      </motion.div>

      {/* Employment Type Info Banner (non-entitled workers) */}
      {!canManageAttendance && !loadingEmploymentType && !isEligibleForLeave && employmentType && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="relative bg-white rounded-xl border border-blue-200 overflow-hidden"
        >
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-blue-500" />
          <div className="p-5 pl-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800">Unavailability Requests</h3>
                <p className="text-sm text-slate-600 mt-1">
                  As a <EmploymentTypeBadge type={employmentType} size="sm" className="mx-1 inline-flex" /> you can declare dates you are unavailable to work, but you are not eligible for paid leave entitlements.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Admin: User Selection */}
      {canManageAttendance && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-xl border border-slate-200 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Users2 className="h-4.5 w-4.5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Request Leave For</p>
                  <p className="text-xs text-slate-500">Select one or more team members</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedUserIds(companyUsers.map(u => u.id));
                    setLeaveRequests([]);
                  }}
                  disabled={loadingPageData || selectedUserIds.length === companyUsers.length}
                  className="text-xs h-8"
                >
                  Select All
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedUserIds([]);
                    setLeaveRequests([]);
                  }}
                  disabled={loadingPageData || selectedUserIds.length === 0}
                  className="text-xs h-8"
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>

          <div className="max-h-[280px] overflow-y-auto">
            {loadingPageData ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : companyUsers.length === 0 ? (
              <p className="text-sm text-slate-500 p-6 text-center">No users found</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {companyUsers.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleUserSelectionChange(user.id, !selectedUserIds.includes(user.id))}
                    className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors
                      ${selectedUserIds.includes(user.id) ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}
                  >
                    <Checkbox
                      checked={selectedUserIds.includes(user.id)}
                      onCheckedChange={(checked) => handleUserSelectionChange(user.id, checked)}
                      onClick={(e) => e.stopPropagation()}
                      className="data-[state=checked]:bg-slate-800 data-[state=checked]:border-slate-800"
                    />
                    <span className="text-sm font-medium text-slate-700 flex-1">{user.name}</span>
                    <span className="text-xs text-slate-400">{user.email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(selectedUserIds.length > 0 || (!loadingPageData && selectedUserIds.length === 0)) && (
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-100">
              {selectedUserIds.length > 0 ? (
                <p className="text-xs font-medium text-slate-600">
                  {selectedUserIds.length} {selectedUserIds.length === 1 ? 'person' : 'people'} selected
                </p>
              ) : (
                <p className="text-xs text-amber-600">Please select at least one person</p>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Balance Card */}
      {shouldShowBalance && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-slate-200 p-5"
        >
          {selectedUserIds.length > 1 ? (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <Users2 className="h-6 w-6 text-slate-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">Multiple Users Selected</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Leave requests will be created for {selectedUserIds.length} users
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-0.5">
                    {canManageAttendance && selectedUserIds.length === 1
                      ? `${companyUsers.find(u => u.id === selectedUserIds[0])?.name}'s Balance`
                      : 'Your Leave Balance'
                    }
                  </p>
                  {loadingBalance || loadingPageData ? (
                    <div className="h-7 w-20 bg-slate-200 rounded animate-pulse" />
                  ) : balance ? (
                    <p className="text-2xl font-bold text-slate-800 tabular-nums">
                      {balance.remaining_days} <span className="text-sm font-medium text-slate-500">days remaining</span>
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500">No balance found</p>
                  )}
                </div>
              </div>

              {balance && !loadingBalance && (
                <div className="text-right space-y-1">
                  <p className="text-xs text-slate-500">
                    <span className="text-slate-400">Entitled:</span>{' '}
                    <span className="font-medium text-slate-600 tabular-nums">{balance.entitled_days}</span>
                  </p>
                  <p className="text-xs text-slate-500">
                    <span className="text-slate-400">Taken:</span>{' '}
                    <span className="font-medium text-slate-600 tabular-nums">{balance.taken_days}</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Leave Details Form */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-xl border border-slate-200"
      >
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Leave Details</h2>
          <p className="text-xs text-slate-500 mt-0.5">Fill in the details for your leave request</p>
        </div>

        <form onSubmit={handleAddToList} className="p-5 space-y-5">
          {/* Leave Type */}
          <div className="space-y-2">
            <Label htmlFor="leave_type" className="text-sm font-medium text-slate-700">Leave Type</Label>
            {loadingPageData ? (
              <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
            ) : (
              <Select
                value={formData.leave_type}
                onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
              >
                <SelectTrigger className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {availableLeaveTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: type.color }}
                        />
                        <span>{type.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedLeaveType?.description && (
              <p className="text-xs text-slate-500">{selectedLeaveType.description}</p>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            {/* Start Date */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Start Date</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                required
              />
              <Select
                value={formData.start_period}
                onValueChange={(value) => setFormData({ ...formData, start_period: value })}
              >
                <SelectTrigger className="h-9 text-xs rounded-lg border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Day</SelectItem>
                  <SelectItem value="am">Morning (AM)</SelectItem>
                  <SelectItem value="pm">Afternoon (PM)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">End Date</Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                required
              />
              <Select
                value={formData.end_period}
                onValueChange={(value) => setFormData({ ...formData, end_period: value })}
                disabled={formData.start_date === formData.end_date}
              >
                <SelectTrigger className="h-9 text-xs rounded-lg border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Day</SelectItem>
                  <SelectItem value="am">Morning (AM)</SelectItem>
                  <SelectItem value="pm">Afternoon (PM)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Warnings */}
          <AnimatePresence>
            {warnings.pastDate && (
              <AlertMessage
                type="error"
                title="Cannot request leave for past dates"
                description="Please select a start date from today onwards."
              />
            )}

            {warnings.overlap && (
              <AlertMessage
                type="warning"
                title="Date range overlaps with another request"
                description={`This conflicts with your request for ${warnings.overlapDetails}.`}
              />
            )}

            {warnings.insufficientBalance && (
              <AlertMessage
                type="warning"
                title="Insufficient annual leave balance"
                description={`This would exceed your remaining ${balance?.remaining_days || 0} days.`}
              />
            )}

            {currentUserOverlap && (
              <AlertMessage
                type="error"
                title="Leave already booked during this period"
                description={`${canManageAttendance && selectedUserIds[0] && selectedUserIds[0] !== userProfile?.id
                  ? companyUsers.find(u => u.id === selectedUserIds[0])?.name + ' has'
                  : 'You have'} ${currentUserOverlap.leave_type?.toLowerCase()} leave from ${formatDate(currentUserOverlap.start_date)} to ${formatDate(currentUserOverlap.end_date)}.`}
              />
            )}
          </AnimatePresence>

          {/* Days Preview */}
          {previewDays !== null && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-50 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                  <CalendarDays className="h-5 w-5 text-slate-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">
                    {previewDays} working {previewDays === 1 ? 'day' : 'days'}
                  </p>
                  {formData.leave_type === 'Annual Leave' && balance && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {previewDays <= balance.remaining_days
                        ? `${balance.remaining_days - previewDays} days will remain after this request`
                        : <span className="text-red-600 font-medium">Exceeds balance by {previewDays - balance.remaining_days} days</span>
                      }
                    </p>
                  )}
                  {workingDays && (
                    <p className="text-xs text-slate-400 mt-1">
                      Working pattern: {formatWorkingDays(workingDays)}
                    </p>
                  )}
                </div>
                {validating && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
            </motion.div>
          )}

          {/* Users on Leave */}
          {formData.start_date && formData.end_date && (
            <div>
              {validating ? (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  <p className="text-xs text-slate-500">Checking team availability...</p>
                </div>
              ) : usersOnLeave.length > 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-amber-800 mb-2">
                    {usersOnLeave.length} {usersOnLeave.length === 1 ? 'person' : 'people'} also off during this period
                  </p>
                  <div className="space-y-1.5">
                    {usersOnLeave.map((user, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-amber-900">{user.name}</span>
                        <span className="text-amber-600">
                          {formatDate(user.startDate)} → {formatDate(user.endDate)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <p className="text-sm text-emerald-800">No team conflicts during this period</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-medium text-slate-700">
              Notes <span className="font-normal text-slate-400">(optional)</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="Add any additional context for your request..."
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              rows={3}
              className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400 resize-none"
            />
          </div>

          {/* Add Button */}
          <Button
            type="submit"
            disabled={!previewDays || warnings.pastDate || warnings.overlap || warnings.insufficientBalance || currentUserOverlap || validating}
            className="w-full h-11 bg-slate-800 hover:bg-slate-700 text-white rounded-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add {leaveRequests.length > 0 ? 'Another Period' : 'to Request'}
          </Button>
        </form>
      </motion.div>

      {/* Added Requests List */}
      <AnimatePresence>
        {leaveRequests.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="bg-white rounded-xl border border-slate-200"
          >
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Ready to Submit</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {leaveRequests.length} {leaveRequests.length === 1 ? 'period' : 'periods'} added
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Total</p>
                <p className="text-xl font-bold text-slate-800 tabular-nums">{totalDays} days</p>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <AnimatePresence>
                {leaveRequests.map((request, index) => (
                  <LeaveRequestItem
                    key={request.id}
                    request={request}
                    leaveTypes={leaveTypes}
                    formatDate={formatDate}
                    formatPeriodSuffix={formatPeriodSuffix}
                    onRemove={handleRemoveRequest}
                    index={index}
                  />
                ))}
              </AnimatePresence>
            </div>

            <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/attendance`)}
                className="flex-1 h-11 rounded-lg border-slate-200"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmitAll}
                disabled={submitting}
                className="flex-1 h-11 bg-slate-800 hover:bg-slate-700 text-white rounded-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Help Section */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-slate-200 p-5"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <Info className="h-4 w-4 text-slate-500" />
          </div>
          <p className="text-sm font-medium text-slate-700">Good to know</p>
        </div>
        <ul className="space-y-2.5 text-sm text-slate-600">
          <li className="flex items-start gap-2">
            <ChevronRight className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <span>Weekends and bank holidays are automatically excluded</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <span>Half-day requests (AM/PM) are available for flexibility</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <span>Annual leave requires sufficient balance available</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <span>Your manager will review and approve requests</span>
          </li>
        </ul>
      </motion.div>
    </div>
  );
};
